/**
 * Settings API
 *
 * API endpoints for settings management operations
 */

import toast from "react-hot-toast";

import { nextCargoApi } from "../../baseApi";
import type {
  CreateSettingsRecipientsByCompany,
  GetMailSettingsByCompany,
  GetSettingsByCompany,
  GetSettingsRecipientsByCompany,
  PatchMailSettingsByCompany,
  PatchSettingsRecipientsByCompany,
  PostMailSettingsByCompany,
  UpdateSettingsArgs,
  UploadLogoSettings,
  UploadLogoSettingsArgs
} from "./settings.types";

/**
 * Settings API endpoints
 * Provides operations for settings management
 */
export const settingsApi = nextCargoApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Get settings by company ID
     * @param id - Company ID
     * @returns Settings details
     */
    getSettingsByCompany: build.query<GetSettingsByCompany["raw"], GetSettingsByCompany["args"], GetSettingsByCompany["result"]>({
      query: ({ id }) => `/settings/company/${id}`,
      transformResponse: (response) => ({
        ...response,
        weekDays: JSON.parse(response.weekDays) as unknown as GetSettingsByCompany["raw"]["weekDays"],
        googleMaps: response.googleMaps.toLowerCase()
      }),
      onQueryStarted: async (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Carregando configurações...",
          success: "Configurações carregadas com sucesso!",
          error: "Erro ao carregar configurações."
        });
      }
    }),

    /**
     * Update settings
     * @param payload - Settings data to update
     * @returns void
     */
    updateSettings: build.mutation<GetSettingsByCompany["result"], UpdateSettingsArgs>({
      query: ({ weekDays, ...body }) => ({
        url: `/settings/save`,
        method: "POST",
        body: {
          ...body,
          weekDays: JSON.stringify(weekDays)
        },
        responseHandler: "text"
      }),
      onQueryStarted: async (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Salvando configurações...",
          success: "Configurações salvas com sucesso!",
          error: "Erro ao salvar configurações."
        });
      }
    }),

    /**
     * Upload logo for settings
     * @param logo - Logo file
     * @param id - Company ID
     * @returns Upload result
     */
    uploadLogoSettingsByCompany: build.mutation<UploadLogoSettings, UploadLogoSettingsArgs>({
      query: (body) => {
        const formData = new FormData();
        formData.append("logo", body.logo);
        formData.append("company", body.id.toString());

        return {
          url: "/settings/upload",
          method: "POST",
          body: formData
        };
      },
      onQueryStarted: async (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Enviando logo...",
          success: "Logo enviada com sucesso!",
          error: "Erro ao enviar logo."
        });
      }
    }),
    getSettingsRecipientsByCompany: build.query<GetSettingsRecipientsByCompany["result"], GetSettingsRecipientsByCompany["args"]>({
      query: ({ id }) => ({
        url: `/settings/alert-recipients/${id}`,
        method: "GET"
      })
    }),
    postSettingsRecipientsByCompany: build.mutation<CreateSettingsRecipientsByCompany["result"], CreateSettingsRecipientsByCompany["args"]>({
      query: ({ id, ...body }) => ({
        url: `/settings/alert-recipients/${id}`,
        method: "POST",
        body
      }),
      onQueryStarted: async (_args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Salvando destinatários...",
          success: "Destinatários salvos com sucesso!",
          error: "Erro ao salvar destinatários."
        });
      }
    }),
    patchSettingsRecipientsByCompany: build.mutation<PatchSettingsRecipientsByCompany["result"], PatchSettingsRecipientsByCompany["args"]>({
      query: ({ id, ...body }) => ({
        url: `/settings/alert-recipients/${id}`,
        method: "PATCH",
        body
      }),
      onQueryStarted: async (_args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Salvando destinatários...",
          success: "Destinatários salvos com sucesso!",
          error: "Erro ao salvar destinatários."
        });
      }
    }),
    getMailSettingsByCompany: build.query<GetMailSettingsByCompany["result"], GetMailSettingsByCompany["args"]>({
      query: ({ id }) => `/settings/mail/${id}`
    }),
    postMailSettingsByCompany: build.mutation<PostMailSettingsByCompany["result"], PostMailSettingsByCompany["args"]>({
      query: ({ id, ...body }) => ({
        url: `/settings/mail/${id}`,
        method: "POST",
        body
      }),
      onQueryStarted: async (_args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Salvando configurações de e-mail...",
          success: "Configurações de e-mail salvas com sucesso!",
          error: "Erro ao salvar configurações de e-mail."
        });
      }
    }),
    patchMailSettingsByCompany: build.mutation<PatchMailSettingsByCompany["result"], PatchMailSettingsByCompany["args"]>({
      query: ({ id, ...body }) => ({
        url: `/settings/mail/${id}`,
        method: "PATCH",
        body
      }),
      onQueryStarted: async (_args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Salvando configurações de e-mail...",
          success: "Configurações de e-mail salvas com sucesso!",
          error: "Erro ao salvar configurações de e-mail."
        });
      }
    })
  })
});

/**
 * Enhance endpoints with cache tag configuration
 */
settingsApi.enhanceEndpoints({
  addTagTypes: ["Settings", "SettingsByCompany", "SettingsRecipients", "MailSettingsByCompany"],
  endpoints: {
    getSettingsByCompany: {
      providesTags: (result, error, { id }) => [{ type: "SettingsByCompany", id }]
    },
    updateSettings: {
      invalidatesTags: (result, error, { id }) => {
        if (!error)
          return [
            { type: "SettingsByCompany", id },
            { type: "Settings", id }
          ];
        return [];
      }
    },
    uploadLogoSettingsByCompany: {
      invalidatesTags: (result, error, { id }) => {
        if (!error)
          return [
            { type: "SettingsByCompany", id },
            { type: "Settings", id }
          ];
        return [];
      }
    },
    getSettingsRecipientsByCompany: {
      providesTags: (result, error, { id }) => [{ type: "SettingsRecipients", id }]
    },
    postSettingsRecipientsByCompany: {
      invalidatesTags: (result, error, { id }) => {
        if (!error)
          return [
            { type: "Settings", id },
            { type: "SettingsByCompany", id },
            { type: "SettingsRecipients", id }
          ];
        return [];
      }
    },
    patchSettingsRecipientsByCompany: {
      invalidatesTags: (result, error, { id }) => {
        if (!error)
          return [
            { type: "Settings", id },
            { type: "SettingsByCompany", id },
            { type: "SettingsRecipients", id }
          ];
        return [];
      }
    },
    getMailSettingsByCompany: {
      providesTags: (result, error, { id }) => [{ type: "MailSettingsByCompany", id }]
    },
    postMailSettingsByCompany: {
      invalidatesTags: (result, error, { id }) => {
        if (!error) return [{ type: "MailSettingsByCompany", id }, "Settings", "SettingsByCompany"];
        return [];
      }
    },
    patchMailSettingsByCompany: {
      invalidatesTags: (result, error, { id }) => {
        if (!error) return [{ type: "MailSettingsByCompany", id }, "Settings", "SettingsByCompany"];
        return [];
      }
    }
  }
});
